import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import ErrorView, { StyledCenterView } from '../../common/views/ErrorView';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskTypeDetailCard from './cards/TaskTypeDetailCard';
import { useDeleteTaskType, useGetTaskTypes } from './TaskType.hooks';
import { TaskType } from './TaskType.interface';

const TaskTypeDetailPage: React.FC = () => {
  const history = useHistory();
  const { name } = useParams<{ name?: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [taskType, setTaskType] = useState<TaskType | undefined>();
  const [
    { data, isLoading, error: findError },
    getTaskTypes
  ] = useGetTaskTypes();
  const [
    { data: deletedTaskType, isLoading: isDeleting, error: deleteError },
    del
  ] = useDeleteTaskType(taskType?.name || '');

  useEffect(() => {
    name && getTaskTypes({ name });
  }, [name, getTaskTypes]);

  useEffect(() => {
    const taskType: TaskType | undefined = data?.[0];
    taskType && setTaskType(taskType);
  }, [data]);

  useEffect(() => {
    deletedTaskType && history.push(`/portal/admin/task-types`);
  }, [history, deletedTaskType]);

  useEffect(() => {
    setError(findError || deleteError);
  }, [findError, deleteError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>{taskType ? taskType.label : 'Task Type Detail'}</h2>
        <div style={{ flex: 1 }} />
        <PageTemplateHeaderActions>
          {taskType && (
            <Button
              color='primary'
              variant='contained'
              disabled={isDeleting}
              endIcon={
                isDeleting && <CircularProgress color='primary' size={20} />
              }
              onClick={() => setOpen(true)}
            >
              Remove Task Type
            </Button>
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {!taskType && !isLoading && (
          <ErrorView
            title='Task Type Not Found'
            description='This Task Type is either not available or does not exist.'
            icon={<WarningIcon color='disabled' fontSize='inherit' />}
          />
        )}
        {!taskType && isLoading && (
          <StyledCenterView>
            <div className='content'>
              <CircularProgress color='primary' size={64} />
            </div>
          </StyledCenterView>
        )}
        {taskType && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TaskTypeDetailCard
                    taskType={taskType}
                    onError={setError}
                    onChange={(newTaskType: Partial<TaskType>) => {
                      taskType && setTaskType({ ...taskType, ...newTaskType });
                      setDidUpdate(true);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <ConfirmDialog
        open={open && !!taskType}
        title='Remove Task Type'
        message='Are you sure you want to remove this Task Type?'
        confirmLabel='Remove'
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          setOpen(false);
          del();
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Task Type details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default TaskTypeDetailPage;
